<template>
  <card-minimal id="player" class="flex">
    <div class="inner" :data-orientation="broadcast.metadata.orientation">
      <template v-if="video && streamState === ''">
        <Video :data="video" @duration="timeUpdate" ref="player" :class="`${stream === 'input' ? 'hide-time' : ''}`" />
        <FormDropdown class="switcher" v-if="['live', 'liveStopping'].includes(lifeCycleStatus) && previewUrl !== ''" :items="streamSwitch" @input="streamSwitch = $event.value" />
        <div class="active-sessions">
          <i class="icon-eye" />
          {{ activeSessions }}
        </div>
      </template>
      <template v-else>
        <div class="video-placeholder">
          <div class="loading-spinner" v-if="streamState === 'loading'"></div>
          <p>{{ streamMessage || 'Preview available when the stream is active' }}</p>
        </div>
      </template>

      <div class="player-information">
        <div class="broadcast-info" :data-large="broadcastIsLive || broadcastInTransition || lifeCycleStatus === 'complete'">
          <h2>
            <template v-if="broadcastIsLive">Broadcast is live for</template>
            <template v-else-if="broadcastInTransition && lifeCycleStatus !== 'liveStopping'">Going live in</template>
            <template v-else-if="lifeCycleStatus === 'ready'">Scheduled to start in</template>
            <template v-else-if="lifeCycleStatus === 'complete'">Broadcast length</template>
          </h2>
          <p class="broadcast-time">{{ time }}</p>
        </div>
        <!--<div class="stream-info" v-if="!['complete', 'abandoned'].includes(lifeCycleStatus)">
          <h2>Stream status</h2>
          <div :class="`stream-status ${(['liveStarting', 'live', 'liveStopping'].includes(lifeCycleStatus) || broadcastInTransition) && streamStatus !== 'active' ? 'red' : streamStatus === 'active' ? 'green' : ''}`">
            <i class="icon-stream-20" />
            <h3>
              <template v-if="(['liveStarting', 'live', 'liveStopping'].includes(lifeCycleStatus) || broadcastInTransition) && streamStatus !== 'active'">
                Not receiving stream
              </template>
              <template v-else-if="streamStatus === 'active'">
                Receiving stream
              </template>
              <template v-else>
                Inactive stream
              </template>
            </h3>
          </div>
        </div>-->
        <div class="stream-graph" v-if="metrics.data.datasets[0].data.length > 0">
          <h2>Bitrate graph</h2>
          <div class="graph-container">
            <Graph :data="metrics.data" ref="graph" :options="metricsOptions" v-if="metrics.data.datasets[0].data.length > 0" />
            <template v-else>
              <p class="no-data">No data available</p>
            </template>
          </div>
        </div>
        <div class="broadcast-action" v-if="!['complete', 'abandoned'].includes(lifeCycleStatus)">
          <Button
            :icon="broadcastIsLive ? 'stop' : lifeCycleStatus === 'ready' ? 'play' : ''"
            :class="`${broadcastIsLive || lifeCycleStatus === 'ready' ? 'live-red' : 'only-text black'} f-w`"
            @click="broadcastIsLive || lifeCycleStatus === 'ready' ? startOrStopBroadcast() : null"
          >
            <template v-if="broadcastIsLive">
              Stop broadcast
            </template>
            <template v-else-if="broadcastInTransition">
              <template v-if="['liveStopping'].includes(lifeCycleStatus)">
                Broadcast stopping...
              </template>
              <template v-else>
                Broadcast starting...
              </template>
            </template>
            <template v-else-if="lifeCycleStatus === 'ready'">
              Start broadcast
            </template>
          </Button>
        </div>
      </div>
    </div>
  </card-minimal>
</template>

<script>

import moment from 'moment';

import { StreamsService } from '@/services/streams.service';
import { AnalyticsService } from '@/services/analytics.service';

import Video from '@/components/Player/Video.vue';

export default {
  components: {
    Video,
  },
  computed: {
    /**
     * Get the current broadcast in store
     * @returns {Object}
     */
    broadcast() {
      return this.$store.getters['Broadcast/current'];
    },
    broadcastIsLive() {
      return ['live'].includes(this.lifeCycleStatus) || (['liveStarting'].includes(this.lifeCycleStatus) && !this.broadcast.actual_start_time.includes('0001-01-01'));
    },
    broadcastInTransition() {
      return ['liveStarting', 'live', 'liveStopping'].includes(this.lifeCycleStatus) || ['starting', 'stopping'].includes(this.broadcastState);
    },
    lifeCycleStatus() {
      return this.broadcast?.status?.life_cycle_status;
    },
    streamStatus() {
      return this.broadcast?.stream?.status || '';
    },
    limitedTranscoding() {
      if (this.streamStatus !== 'active' && !['liveStarting', 'live', 'liveStopping'].includes(this.lifeCycleStatus)) {
        return false;
      }

      if (this.accountSettings?.settings) {
        if (this.accountSettings?.settings?.shopping?.enabled) {
          return false;
        }

        if (this.accountSettings?.settings?.subscription?.trial) {
          return true;
        }
      }

      return false;
    },
    streamSwitch: {
      get() {
        const { stream, lifeCycleStatus } = this;
        return [
          {
            value: 'input',
            string: 'Input stream',
            selected: stream === 'input',
          },
          {
            value: 'output',
            string: 'Output stream',
            selected: stream === 'output',
            disabled: ['ready', 'liveStarting'].includes(lifeCycleStatus),
          },
        ];
      },
      set(stream) {
        this.stream = stream;

        if (stream === 'input') {
          this.fetchPreviewUrl();
        } else {
          this.$refs.player.resetup();
        }
      },
    },
    /**
     * Return object for video
     * @returns {Object}
     */
    video() {
      const {
        broadcast: {
          resources,
          id,
          status: {
            life_cycle_status,
          },
        },
        fqdn,
        stream,
        previewUrl,
      } = this;

      const source = { src: '', type: '' };

      const [formatP] = resources.filter(resource => resource.role.search(/^video.*p$/) >= 0 && resource.role.replace(/\D/g, '') <= 720).sort((a, b) => b.role.replace(/\D/g, '') - a.role.replace(/\D/g, ''));
      if (formatP) {
        source.src = Object.values(formatP)[0].substring(0, 4) === 'http' ? Object.values(formatP)[0] : `https://${fqdn}/resources/${Object.values(formatP)[0]}`;
        source.type = 'video/mp4';
      }

      const [hls] = resources.filter(resource => (resource.role === 'hls' ? resource : null));
      if (hls) {
        source.src = `https://${fqdn}.streamify.io/resources/${hls.value}`;
        source.type = 'application/x-mpegURL';
      }

      const [format] = resources.filter(resource => resource.role.search(/^video.*[^p]$/) >= 0 && resource.role.replace(/\D/g, '') <= 720).sort((a, b) => b.role.replace(/\D/g, '') - a.role.replace(/\D/g, ''));
      if (format) {
        source.src = Object.values(format)[0].substring(0, 4) === 'http' ? Object.values(format)[0] : `https://${fqdn}/resources/${Object.values(format)[0]}`;
        source.type = 'video/mp4';
      }

      if (life_cycle_status === 'complete' || (stream === 'output' && ['live', 'liveStopping'].includes(life_cycle_status))) {
        source.src = `https://${fqdn}.streamify.io/resources/${id}.m3u8`;
        source.type = 'application/x-mpegURL';
      }

      if (stream === 'input' && previewUrl !== '') {
        source.src = this.previewUrl;
        source.type = 'application/x-mpegURL';
      }

      if (source.src === '') {
        return null;
      }

      return {
        autoplay: true,
        controls: true,
        fluid: true,
        liveui: true,
        sources: [source],
      };
    },
  },
  methods: {
    goto(time) {
      if (this.$refs.player) {
        this.$refs.player.goto(time);
      }
    },
    async fetchPreviewUrl() {
      let resetupPlayer = true;

      if (this.previewUrl === '') {
        // We dont have a preview url, so lets add some loading stuff
        resetupPlayer = false;
        this.streamState = 'loading';
        this.streamMessage = this.$t('broadcast.fetching_preview');
      }

      try {
        const { data: { url } } = await ovpFetch(`/live/stream/${this.broadcast.stream.id}/preview`);
        this.previewUrl = url;

        if (resetupPlayer) {
          this.$refs.player.resetup();
        } else {
          setTimeout(() => {
            this.streamState = '';
            this.streamMessage = '';
          }, 3000);
        }
      } catch (e) {
        this.stream = 'output';
      }
    },
    async getActiveViewers() {
      const { active_sessions } = await AnalyticsService.range(this.broadcast.id, moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD'));
      this.activeSessions = active_sessions;
    },
    async startOrStopBroadcast() {
      const { lifeCycleStatus, broadcastState } = this;
      if (['liveStarting', 'liveStopping'].includes(lifeCycleStatus) || ['stopping', 'starting'].includes(broadcastState)) {
        return;
      }

      const proceed = await swal({
        title: 'Are you sure?',
        text: lifeCycleStatus === 'ready' ? 'This action will change the start time of the broadcast to three minutes from the current time' : 'This will stop the broadcast. You can not resume the broadcast after it has stopped.',
        icon: 'info',
        buttons: true,
        dangerMode: true,
        className: 'center-content',
      });

      if (!proceed) {
        return;
      }

      if (lifeCycleStatus === 'ready') {
        this.broadcastState = 'starting';
        const startTime = moment().add(3.15, 'm');
        const start = await this.$store.dispatch('Broadcast/update', {
          id: this.broadcast.id,
          data: {
            scheduled_start_time: startTime.toISOString(),
          },
        });

        if (start !== true) {
          swal({
            title: 'An error occured',
            text: 'For some reason we could not start this broadcast right now',
            icon: 'error',
            className: 'center-content',
          });
          this.broadcastState = '';
        }
      } else {
        this.broadcastState = 'stopping';
        const stopped = await this.$store.dispatch('Broadcast/stop', this.broadcast.id);
        if (stopped !== true) {
          this.broadcastState = '';
          swal({
            title: 'An error occured',
            text: 'For some reason we could not stop this broadcast right now',
            icon: 'error',
            className: 'center-content',
          });
        }
      }
    },
    timeUpdate(override = null) {
      if (!this.broadcast) {
        return;
      }
      const {
        lifeCycleStatus,
        broadcast: {
          scheduled_start_time,
          actual_start_time,
        },
      } = this;

      let a = '';
      let b = '';
      let duration = 0;

      if (['ready', 'liveStarting'].includes(lifeCycleStatus)) {
        a = new Date(scheduled_start_time);
        b = new Date();
        duration = moment.duration(Math.abs(a - b), 'millisecond');
      } else if (['live', 'liveStopping'].includes(lifeCycleStatus)) {
        if (actual_start_time.includes('0001-01-01')) {
          this.time = '00:00:00';
          return;
        }

        a = new Date();
        b = new Date(actual_start_time);
        duration = moment.duration(Math.abs(a - b), 'millisecond');
      } else {
        // eslint-disable-next-line
        if (isNaN(override)) {
          this.time = 'N/A';
          return;
        }
        duration = moment.duration(override * 1000, 'millisecond');
      }

      const hours = Math.floor(duration.asHours()) >= 10 ? Math.floor(duration.asHours()) : `0${Math.floor(duration.asHours())}`;
      const minutes = Math.floor(duration.minutes()) >= 10 ? Math.floor(duration.minutes()) : `0${Math.floor(duration.minutes())}`;
      const seconds = Math.floor(duration.seconds()) >= 10 ? Math.floor(duration.seconds()) : `0${Math.floor(duration.seconds())}`;
      this.time = `${hours}:${minutes}:${seconds}`;
    },
    async getMetrics() {
      if (!this.metrics.start) {
        return;
      }

      const metrics = await StreamsService.getMetrics({
        id: this.broadcast.stream_id,
        start: this.metrics.start,
        end: this.metrics.end || moment().utc().format(),
        step: 30,
      });

      const labels = metrics.map(m => moment(m.timestamp).format('HH:mm:ss'));
      const data = metrics.map(m => Math.round(m.value));
      const maxValue = Math.max(...data) + 500;

      if (data.length === 1) {
        data.push(data[0]);
        labels.push(labels[0]);
      }

      if (this.metricsOptions.scales.y.max < maxValue) {
        this.metricsOptions.scales.y.max = Math.ceil(maxValue / 1000) * 1000;
      }

      this.metrics.data.labels = labels;
      this.metrics.data.datasets[0].data = data;
    },
  },
  watch: {
    streamStatus: {
      immediate: true,
      async handler(streamStatus) {
        clearInterval(this.metrics.interval);
        const { lifeCycleStatus } = this;
        if (lifeCycleStatus === 'complete') {
          if (!this.metrics.start) {
            this.metrics.start = moment(this.broadcast.actual_start_time).subtract(3, 'minutes').utc().format();
            this.metrics.end = moment(this.broadcast.actual_end_time).utc().format();
            this.$nextTick(() => {
              this.getMetrics();
            });
          }
          return;
        }

        if (streamStatus === 'active') {
          if (['ready', 'liveStarting', 'live'].includes(lifeCycleStatus)) {
            this.stream = 'input';
            this.metrics.interval = setInterval(() => {
              this.getMetrics();
            }, 30000);

            if (['ready', 'liveStarting'].includes(lifeCycleStatus)) {
              this.metrics.start = moment().utc().format();
            }
          }

          this.$nextTick(() => {
            this.getMetrics();
          });

          await this.fetchPreviewUrl();
        }

        if (streamStatus === 'inactive') {
          if (['ready'].includes(lifeCycleStatus)) {
            this.previewUrl = '';
          }
        }
      },
    },
    lifeCycleStatus: {
      immediate: true,
      handler(status, oldStatus) {
        clearInterval(this.timeInterval);

        switch (status) {
          case 'complete':
            this.broadcastState = '';
            clearInterval(this.metrics.interval);

            if (['liveStopping', 'live'].includes(oldStatus) && this.stream === 'input') {
              this.streamState = 'loading';
              this.streamMessage = 'Loading the completed broadcast';
              this.streamTimeout = setTimeout(() => {
                this.stream = 'output';
                this.streamState = '';
                this.streamMessage = '';
              }, 5000);
            }
            break;
          case 'live':
          case 'liveStarting':
            this.broadcastState = '';
            if (!this.metrics.start) {
              this.metrics.start = moment(this.broadcast.actual_start_time).subtract(3, 'minutes').utc().format();
            }
            this.timeUpdate();
            this.timeInterval = setInterval(this.timeUpdate, 1000);
            break;
          case 'liveStopping':
            this.timeUpdate();
            this.timeInterval = setInterval(this.timeupdate, 1000);
            if (!this.metrics.start) {
              this.metrics.start = moment(this.broadcast.actual_start_time).subtract(3, 'minutes').utc().format();
            }
            break;
          default:
            this.timeUpdate();
            this.timeInterval = setInterval(this.timeUpdate, 1000);
            break;
        }
      },
    },
  },
  data() {
    return {
      broadcastState: '',
      stream: '',
      streamState: '',
      streamMessage: '',
      streamTimeout: null,
      previewUrl: '',
      viewerInterval: null,
      activeSessions: 0,
      time: '',
      timeInterval: null,
      metrics: {
        start: null,
        end: null,
        data: {
          labels: [],
          datasets: [
            {
              backgroundColor: '#0060ff',
              borderColor: '#0060ff',
              label: 'Metrics',
              data: [],
              tension: 0.2,
              pointRadius: 0,
              pointHitRadius: 20,
            },
          ],
        },
        interval: null,
      },
      metricsOptions: {
        scales: {
          y: {
            min: 0,
            max: 3500,
            grid: {
              color: 'rgb(244,246,254, 1)',
            },
            ticks: {
              display: true,
            },
          },
          x: {
            ticks: {
              display: false,
            },
          },
        },
      },
    };
  },
  mounted() {
    if (this.broadcast.metadata.orientation === 'portrait') {
      this.metricsOptions.scales.y.ticks.display = false;
      this.metricsOptions.scales.y.grid.color = 'rgba(0, 0, 0, 0)';
    }

    this.getActiveViewers();
    this.viewerInterval = setInterval(this.getActiveViewers, 5000);
  },
  beforeDestroy() {
    clearInterval(this.timeInterval);
    clearTimeout(this.streamTimeout);
    clearInterval(this.viewerInterval);
    clearInterval(this.metrics.interval);
  },
};
</script>

<style lang="scss" scoped>
.inner {
  display: flex;
  flex: 1;
  position: relative;

  .video-placeholder {
    align-items: center;
    display: flex;
    justify-content: center;
    flex-direction: column;
    background-color: #f5f5f5;
    text-align: center;
    padding: 0 16px;

    p {
      color: rgba(44,62,79,.5);
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
    }
  }

  .streamify-video {
    overflow: hidden;
  }

  .switcher {
    position: absolute;
    right: 20px;
    top: 20px;
  }

  .transcoding-notice {
    color: white;
    align-items: center;
    justify-content: center;
    text-align: center;
    display: flex;
    gap: 10px;
    justify-content: center;
    background-color: #f73131;
    // box-shadow: 0 1px 0 0 #E8D4BE;
    border-bottom: 1px solid #db1b1b;
    padding: 12px 15px;
    flex-wrap: wrap;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1;

    p {
      font-size: 14px;
    }

    & > * {
      &:not(:last-child) {
        margin-right: 12px;
      }
    }
  }

  .active-sessions {
    position: absolute;
    left: 20px;
    top: 20px;
    background: black;
    border-radius: 4px;
    color: white;
    display: flex;
    align-items: center;
    padding: 7px 12px 7px 10px;
    font-size: 16px;
    line-height: 1;

    i {
      margin-right: 10px;
    }
  }

  &[data-orientation="landscape"] {
    flex-direction: column;

    .video-placeholder, .streamify-video {
      aspect-ratio: 16 / 9;
      border-radius: 10px 10px 0 0;
      width: 100%;
    }

    .player-information {
      flex: 1;
      min-width: 300px;

      .stream-graph {
        .graph-container {
          height: 60px;
          padding: 0 -16px;
          position: relative;

          .graph {
            height: 60px;
            position: absolute !important;
            left: 0;
            right: 0;
            bottom: 0;
            top: 0;
          }
        }
      }
    }

    @media (min-width: 768px) {
      flex-direction: row;

      .video-placeholder, .streamify-video {
        border-radius: 10px 0 0 10px;
      }
    }
  }

  &[data-orientation="portrait"] {
    .video-placeholder, .streamify-video {
      aspect-ratio: 9 / 16;
    }

    .player-information {
      .stream-graph {
        .graph-container {
          height: 60px;
          padding: 0 -16px;

          .graph {
            height: 60px;
          }
        }
      }
    }

    @media (max-width: 767px) {
      .video-placeholder {
        border-right: 1px solid #C1C5D7;
        border-radius: 10px 0 0 10px;
        width: 50%;
      }

      .streamify-video {
        border-radius: 10px 0 0 10px;
        width: 50%;
      }
    }

    @media (min-width: 768px) {
      flex-direction: column;

      .video-placeholder {
        border-bottom: 1px solid #C1C5D7;
        border-radius: 10px 10px 0 0;
        height: calc(100vh - 254px);
      }

      .streamify-video {
        border-radius: 10px 10px 0 0;
        height: calc(100vh - 254px);
      }
    }
  }

  .player-information {
    display: flex;
    flex-direction: column;
    flex: 1;

    h2 {
      color: #727479;
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
    }

    & > * {
      padding: 10px 12px;
    }

    .broadcast-info {
      .broadcast-time {
        font-size: 18px;
        transition: font-size .3s;
        line-height: 1;
      }

      &[data-large="true"] {
        .broadcast-time {
          font-size: 40px;
        }
      }
    }

    .stream-info {
      .stream-status {
        align-items: center;
        display: flex;
        line-height: 1;
        line-height: 1;

        h3 {
          transition: all;
          transition-duration: 500ms;
          transition-delay: 500ms;
          font-size: 18px;
        }

        i {
          transition: all;
          transition-duration: 500ms;
          transition-delay: 500ms;
          margin-right: 10px;
          font-size: 20px;
        }

        &.red {
          color: var(--color-cta-red);
          i {
            font-size: 26px
          }
          h3 {
            font-size: 24px;
          }
        }

        &.green {
          color: var(--color-cta-green);
        }
      }
    }
    .stream-graph {
      padding: 10px 12px 0;
      margin: 0;

      .graph-container {
        height: 180px;
        margin: 0 -15px 0;
        display: flex;
        align-items: center;
        justify-content: center;

        & > * {
          width: 100%;
        }

        .no-data {
          color: #868B97;
          font-size: 16px;
          text-align: center;
        }
      }
    }
    .broadcast-action {
      margin-top: auto;
    }
  }
}
</style>

<style lang="scss">
#player {
  .inner {
    .player-information {
      .stream-graph {
        .graph-container {
          padding: 0 -16px;
          position: relative;

          .graph {
            position: absolute !important;
            left: 0;
            right: 0;
            bottom: 0;
            top: 0;
          }
        }
      }
    }
    &[data-orientation="landscape"] {
      .player-information {
        .stream-graph {
          .graph-container {
            height: 120px;

            .graph {
              height: 120px;
            }
          }
        }
      }
    }
    &[data-orientation="portrait"] {
      .player-information {
        .stream-graph {
          .graph-container {
            height: 60px;

            .graph {
              height: 60px;
            }
          }
        }
      }
    }
  }
}
</style>
